<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="isNew">
        {{ $t('permits.add_permit') }}
      </h1>
      <h1 v-else>
        {{ $t('permits.edit_permit') }}
      </h1>
      <a v-if="!isNew" class="link link--orange" @click="showDeletingConfirmation">
        {{ $t('common.delete') }}&nbsp;
        <i class="icon icon--cross" />
      </a>
    </div>

    <div class="details-component__content-container">
      <div class="details-component__content content" :class="{ sticky: $route.name === isUnderUser }">
        <form class="form fixed-form">
          <table class="details-table no-col-padding edit-table fixed-table label-col-wide-150">
            <tr v-if="canUseCards()" class="details-table__row"
              :class="{ 'details-table__row_error': errors.has('parkingCardNr') }">
              <td class="details-table__col details-table__col_label">
                <label for="parkingCardNr" class="form__label">{{ $t('common.card_number') }}</label>
              </td>
              <td class="details-table__col">
                <el-input id="parkingCardNr" ref="parkingCardNr" v-model="form.parkingCardNr"
                  v-validate="'validParkingCardNr'" name="parkingCardNr" class="parkingCardNrInput-toggle"
                  :data-vv-as="$t('add_permit.parking_card_id')" data-vv-delay="1000" />
                <tooltip v-show="errors.has('parkingCardNr')" class="form__validation">
                  {{ $t('errors.valid_card_field') }}
                </tooltip>
              </td>
            </tr>
            <!-- Parking spot number -->
            <tr v-if="canShowPermitSpot()" class="details-table__row"
              :class="{ 'details-table__row_error': errors.has('parkingSpotNumber') }">
              <td class="details-table__col details-table__col_label">
                <label for="parkingSpotNumber" class="form__label">{{ $t('permits.parking_spot_number') }}</label>
              </td>
              <td class="details-table__col">
                <input id="parkingSpotNumber" ref="parkingSpotNumber" v-model="form.parkingSpotNumber"
                  v-validate="'max:255'" type="text" class="form__input" name="parkingSpotNumber"
                  :data-vv-as="$t('permits.parking_spot_number')" :disabled="!canSetPermitSpot()">
                <tooltip v-show="errors.has('parkingSpotNumber')" class="form__validation">
                  {{ $tc('errors.field_max', 1, {
                    max: 255, have: form.parkingSpotNumber ? form.parkingSpotNumber.length
                      : ''
                  }) }}
                </tooltip>
              </td>
            </tr>

            <tr v-if="(canSetPermitStartDate() || form.validFromDate)" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('permits.valid_from_date') }}
              </td>
              <td class="details-table__col">
                <el-date-picker v-model="form.validFromDate" format="dd.MM.yyyy" type="date" name="validFromDate"
                  :placeholder="defaultDate" :default-value="defaultDateValue" :picker-options="startTimeOptions"
                  :readonly="!canSetPermitStartDate()" :clearable="true" value-format="dd.MM.yyyy" />
              </td>
            </tr>

            <tr v-if="(canSetPermitExpirationDate() || form.expirationDate)" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('permits.expiration_date') }}
              </td>
              <td class="details-table__col">
                <el-date-picker v-model="form.expirationDate" format="dd.MM.yyyy" type="date" name="expirationDate"
                  :picker-options="expireTimeOptions"
                  :readonly="!canSetPermitExpirationDate() || !canUseDateExpiration()" :clearable="true"
                  value-format="dd.MM.yyyy" />
              </td>
            </tr>

            <!-- Vehicles -->
            <tr class="details-table__row details-table__row-—flex">
              <td class="details-table__col details-table__col_label">
                <label for="vehicleReg" class="form__label">{{ $tc('common.user_vehicle_reg', 1) }}</label>
              </td>
              <td class="details-table__col details-table__col--with-a-choise">
                <div class="el-input__inner el-input__inner--vehicle-reg">
                  {{ displayedVehicleReg }}
                </div>
                <el-button slot="append" class="btn btn--change" @click="showListModalVehicle()">
                  {{ selectedVehicle.id ? $t('common.change') + ' ' + $tc('common.user_vehicle_reg', 1) :
                    $t('common.add') + ' ' + $tc('common.user_vehicle_reg', 1) }}
                </el-button>
              </td>
            </tr>

            <!-- Products -->
            <tr v-if="isUnderUser" class="details-table__row details-table__row-—flex">
              <td class="details-table__col details-table__col_label">
                <label for="product" class="form__label">{{ $tc('add_permit.product', 1) }}</label>
              </td>
              <td class="details-table__col details-table__col--with-a-choise">
                <div class="el-input__inner el-input__inner--vehicle-reg">
                  {{ selectedProductName }}
                </div>
                <el-button v-if="products.data" slot="append" class="btn btn--change" @click="showListModalProduct()">
                  {{ selectedProductName ? $t('common.change') + ' ' + $tc('add_permit.product', 1) : $t('common.add') +
                    ' ' + $tc('add_permit.product', 1) }}
                </el-button>
              </td>
            </tr>

            <tr v-if="!isUnderUser" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('common.zone') }}
              </td>
              <td v-if="permit.product" class="details-table__col">
                <span v-for="zone in permit.product.zones" :key="zone.id">{{ zone.code }}&nbsp;</span>
              </td>
            </tr>

            <tr v-if="permit.partner && !isUnderUser" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('common.issued_by') }}
              </td>
              <td class="details-table__col">
                {{ permit.partner.name }}
              </td>
            </tr>

            <tr v-if="permit.partner && !isUnderUser" class="details-table__row">
              <td class="details-table__col details-table__col_label">
                {{ $t('end_user.contact') }}
              </td>
              <td class="details-table__col">
                {{[permit.partner.contactPerson,
                permit.partner.phone
                  ? parsePhoneNumber(permit.partner.phone, permit.partner.phoneCountry)
                  : null,
                permit.partner.address]
                  .filter(x => x !== null)
                  .join(',&nbsp;')}}
              </td>
            </tr>
          </table>

          <div class="form__buttons buttonset">
            <button v-if="isShowPrevPermitBtn()" type="submit" class="buttonset__button btn btn--secondary"
              @click.prevent.stop="onPrevPermit()">
              {{ $t('permits.prev') }}
            </button>
            <button v-if="isShowNextPermitBtn()" type="submit" class="buttonset__button btn btn--secondary"
              @click.prevent.stop="onNextPermit()">
              {{ $t('permits.next') }}
            </button>
            <router-link :to="{ name: 'user', params: { id: $route.params.id } }"
              class="buttonset__button btn btn--secondary">
              {{ $t('common.discard') }}
            </router-link>
            <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
              {{ $t('permits.save') }}
            </button>
          </div>
        </form>

        <v-map v-if="coordinates && coordinates.length > 0 && !isUnderUser" ref="map" :zoom="zoom" :center="center"
          class="content__cell product-map">
          <v-geojson-layer :geojson="coordinates" :options="mapOptions" />
          <v-tilelayer :url="url" />
        </v-map>
      </div>
      <div :key="permit.id" class="permit-block" style="height: 100%">
        <div class="permit-block__zone" style="height: 100%">
          <div>
            <span class="permit-icon icon-product" />
            <span v-if="selectedProductName">{{ selectedProductName }}</span>
            <span v-else>-------</span>
          </div>
          <div>
            <span class="permit-icon fa fa-map-marker" />
            <span v-if="showZone">{{ permit.zone }}</span>
            <span v-else>-------</span>
          </div>
        </div>
        <div class="permit-block__identifiers"
             :class="{'smaller-font': displayedVehicleReg && displayedVehicleReg.length > 6 || (displayedVehicleReg && permit.parkingCardNr)}">
          <div class="permit-block__identifiers__identity"
               :class="{
                    'smaller-font': displayedVehicleReg && displayedVehicleReg.length > 6 || (displayedVehicleReg && permit.parkingCardNr)
                  }"
               v-if="displayedVehicleReg">
            <div class="permit-plate-container">
              <div class="permit-plate" :style="!permit.parkingSpotNumber ? 'padding-bottom: 10px;' : ''">
                <div
                  class="icon car-icon icon-car-white">
                </div>
                <p class="permit-plate-number"
                >{{ displayedVehicleReg }}</p>
              </div>
              <div v-if="permit.parkingSpotNumber" class="permit-spot-number">
                <span class="permit-plate__spot"></span>
                <span>{{ permit.parkingSpotNumber }}</span>
              </div>
            </div>
          </div>
          <div class="parking-card-container" v-if="permit.parkingCardNr" @click.prevent="zoomCard(permit.id)">
            <div class="permit-block__identifiers__identity permit-card permit-card__small"
                 :class="{'permit-card--zoomed': isCardZoomed}">
              <div class="permit-card__number">{{ permit.parkingCardNr }}</div>
            </div>
            <div class="card-magnifier icon-zoom"></div>
          </div>
          <div
            class="permit-block__identifiers__identity no-identity"
            v-if="!displayedVehicleReg && !permit.parkingCardNr"
          >
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            {{ $t('end_user.permit_needs_identity') }}
          </div>
        </div>

        <div v-if="!isPermitExpired(permit) && permitValidityPeriod(permit).length > 0" class="permit-card-validity">
          {{ permitValidityPeriod(permit) }}
        </div>
        <div v-if="isPermitExpired(permit)" class="permit-card-expired">
          {{ $t('end_user.permit_expired', { expirationDate: permit.expirationDate }) }}
        </div>
      </div>
    </div>

    <ListModal v-if="listModalType === 'product'" @cancel="closeListModal" @confirmed="confirmedListModal">
        <tr v-if="isUnderUser" class="details-table__row form__field_radiobuttons auto-height"
          :class="{ 'details-table__row_error': errors.has('product') }">
          <td class="details-table__col">
            <div class="radiobuttons">
              <template v-for="product in products.data">
                <div :key="product.id" class="radiobuttons__element">
                  <label
                    :class="{ 'disabled': !(canAddPermitTo(product) || (permit.product && product.id === permit.product.id)) }"
                    @click="onChangePack(product)">
                    <input :id="product.id" v-model="selectedProduct" v-validate="'required'" type="radio"
                      name="product" :value="product.id"
                      :disabled="!(canAddPermitTo(product) || (permit.product && product.id === permit.product.id))"
                      role="select product">
                    <span class="radiobuttons__control" />
                    <span :for="product.id" class="radiobuttons__description">{{ product.name }} - ({{
                      product.permits_in_use }}/{{ product.permits_count }}) <template
                        v-if="product.date_expiration">({{ $t('permits.expiration_date') }} {{
                          getFormattedDateExpiration(product.date_expiration) }})</template></span>
                  </label>
                </div>
              </template>
            </div>
            <tooltip v-show="errors.has('product')" class="form__validation">
              {{ $t('errors.product_field_required') }}
            </tooltip>
          </td>
        </tr>
    </ListModal>

    <VehicleSelectionModal
        :show="listModalType === 'vehicle'"
        :user-id="selectedUser"
        :active-vehicle-id="selectedVehicle.id"
        @close="closeListModal"
        @confirmed="confirmedVehicleSelection"
        v-if="selectedUser"
      />

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelDelete" @confirmed="deletePermit">
      <h1 slot="header">
        {{ $t('common.deleting') }}
      </h1>
      <p slot="body">
        {{ $t('permits.confirm_delete') }}
      </p>
    </ConfirmationModal>

    <ConfirmationModal v-if="showConfirmationUncheckModal" @cancel="closeConfirmationUncheckModal"
      @confirmed="uncheckVehicleActive">
      <h1 slot="header">
        {{ $t('common.vehicle_reg') }}
      </h1>
      <p slot="body">
        {{ $t('vehicle.confirm_uncheck_active') }}
      </p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">
        {{ userWarningHeader }}
      </h1>
      <p slot="body">
        {{ userWarningBody }}
      </p>
    </WarningModal>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import L from 'leaflet';
import Vue2Leaflet from 'vue2-leaflet';
import { format } from 'libphonenumber-js';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import ListModal from 'common/ListModal';
import VehicleSelectionModal from 'common/VehicleSelectionModal';
import stickybits from 'stickybits';
import { debounce } from 'lodash';

let resolveQueue = [];

const debouncedCheck = debounce((store, payload, context) => {
  store.dispatch('users/vehicles/isDuplicate', payload)
    .then(isDuplicate => {
      resolveQueue.forEach(resolve => resolve({ valid: !isDuplicate }));
      resolveQueue = [];
    })
    .catch(() => {
      resolveQueue.forEach(resolve => resolve({ valid: true }));
      resolveQueue = [];
    });
}, 500);

export default {
  components: {
    'v-map': Vue2Leaflet.Map,
    'v-tilelayer': Vue2Leaflet.TileLayer,
    'v-geojson-layer': Vue2Leaflet.GeoJSON,
    Tooltip,
    ConfirmationModal,
    WarningModal,
    ListModal,
    VehicleSelectionModal,
  },
  props: {
    selectedTenant: Number,
  },
  computed: {
    isNew() {
      return !this.$route.params.permitId;
    },
    displayedVehicleReg() {
      const v = this.selectedVehicle;
      if(v.vehicle_reg){
        return v.vehicle_reg;
      }
      if(v.vehicleReg){
        return v.vehicleReg;
      }

      return '-------';
    },
    ...mapGetters({
      permitError: 'permits/permit/error',
      permit: 'permits/permit/permit',
      coordinates: 'permits/permit/coordinates',
      user: 'user/user/user',
      users: 'users/user/user',
      vehicles: 'users/vehicles/vehicles',
      vehicleError: 'users/vehicles/error',
      products: 'landlord/products/products',
      usersKeywordFilter: 'landlord/users/keywordFilter',
      permits: 'landlord/permits/permits',
    }),
    selectedUser() {
      if (this.isUnderUser) {
        return this.$route.params.id;
      }
      return this.user.data && this.user.data.selectedRole ? this.user.data.selectedRole.id : null;
    },
    isUnderUser() {
      return this.$route.name === 'user-with-permit' || this.$route.name === 'add-user-with-permit';
    },
    defaultDate() {
      return moment().format('DD.MM.YYYY');
    },
    defaultDateValue() {
      return moment().toDate();
    },
  },
  watch: {
    $route: 'fetchAllData',
    selectedTenant: 'fetchTenantData',
    selectedProduct(value) {
      this.setSelectedProductName(value);
    },
  },
  created() {
    if (!this.user.authenticated) {
      return;
    }
    this.fetchAllData();
    this.resetValidators();
    this.createValidators();
  },
  mounted() {
    if (!this.isUnderUser) {
      this.updateMap();
    }
    stickybits('.sticky');
    this.$validator.extend('unique_car_reg', {
      getMessage: () => 'Car registration is not unique',
      validate: value => {
        return new Promise(resolve => {
          const upperValue = value.toUpperCase();

          resolveQueue.push(resolve);

          debouncedCheck(this.$store, {
            id: this.selectedUser,
            user: this.user,
            vehicleReg: upperValue
          }, this);
        });
      }
    });

    this.$validator.attach({
      name: 'vehicleReg',
      rules: 'unique_car_reg',
    });
  },
  updated() {
    stickybits('.sticky');
  },
  destroyed() {
    this.$store.commit(`landlord/products/${mTypes.CLEAR_STATE}`);
  },
  data() {
    return {
      isCardZoomed: false,
      selectedVehicle: {
        id: null,
        vehicle_reg: null,
      },
      primordialSelectedVehicle: this.selectedVehicle,
      form: {},
      tenantHasProducts: false,
      tenantPermitCount: 0,
      selectedProduct: null,
      primordialSelectedProduct: this.selectProduct,
      selectedProductName: null,
      showConfirmationModal: false,
      showConfirmationUncheckModal: false,
      showWarningModal: false,
      userWarningBody: null,
      userWarningHeader: null,
      listModalType: null,
      zoom: 15,
      center: [59.412, 24.707],
      url: 'https://a.tile.openstreetmap.org//{z}/{x}/{y}.png',
      startTimeOptions: {
        disabledDate: date => (moment(date).isAfter(moment(this.form.expirationDate, 'DD.MM.YYYY').add(-1, 'day'), 'day')),
        firstDayOfWeek: 1,
      },
      expireTimeOptions: {
        disabledDate: date => (moment(date).isBefore(moment(this.form.validFromDate, 'DD.MM.YYYY').add(1, 'day'), 'day')),
        firstDayOfWeek: 1,
      },
      mapOptions: {
        style: (feature) => {
          let color = '#E94F0A';
          if (feature.properties.color) {
            color = { color: feature.properties.color };
          } else {
            const index = Math.floor(Math.random() * 5);
            const coordinateColors = ['#E94F0A', '#389DE9', '#fb4f4f', '#00d1b2', '#fbc93d'];
            color = { color: coordinateColors[index] };
          }
          return color;
        },
        onEachFeature: (feature, layer) => {
          if (feature.properties && feature.properties.name) {
            layer.bindPopup(feature.properties.name);
          }
        },
      },
      showZone: false,
    };
  },
  methods: {
    zoomCard() {
      this.isCardZoomed = !this.isCardZoomed;
    },
    getCurrentPermitIndex() {
      if (this.permits.items && this.permits.items.length) {
        return this.permits.items.findIndex(p => p.id === this.permit.id);
      }
      return -1;
    },
    isShowPrevPermitBtn() {
      return this.getCurrentPermitIndex() > 0;
    },
    isShowNextPermitBtn() {
      const currentPermitIndex = this.getCurrentPermitIndex();
      return currentPermitIndex >= 0 && currentPermitIndex < this.permits.items.length - 1;
    },
    onPrevPermit() {
      const currentPermitIndex = this.getCurrentPermitIndex();
      const prevPermitId = this.permits.items[currentPermitIndex - 1].id;
      this.$router.push({ name: 'user-with-permit', params: { id: this.$route.params.id, permitId: prevPermitId } });
    },
    onNextPermit() {
      const currentPermitIndex = this.getCurrentPermitIndex();
      const nextPermitId = this.permits.items[currentPermitIndex + 1].id;
      this.$router.push({ name: 'user-with-permit', params: { id: this.$route.params.id, permitId: nextPermitId } });
    },
    onChangePack(product) {
      if (product.date_expiration) {
        this.form.expirationDate = product.date_expiration;
      }
      if (product.parking_spot_number) {
        this.form.parkingSpotNumber = product.parking_spot_number;
      }
    },
    hasProducts() {
      if (this.selectedTenant || this.isTenant()) {
        let productsNotFull = false;
        this.tenantPermitCount = 0;
        if (this.products.data.length > 0) {
          this.products.data.forEach((el) => {
            if (el.permits_issued < el.permit_count) {
              productsNotFull = true;
            }
            this.tenantPermitCount += el.total;
          });
        }
        this.tenantHasProducts = this.products.data.length > 0 && productsNotFull;

        if (this.isNew && !this.tenantHasProducts && this.tenantPermitCount > 0) {
          this.showWarningModal = true;
          this.userWarningHeader = this.$t('common.warning');
          this.userWarningBody = this.$t('errors.all_permits_issued', { permitCount: this.tenantPermitCount });
        }

        this.permitZoneAdd();
      }
      this.setSelectedProductName(this.selectedProduct);
    },
    fetchTenantData() {
      this.fetchProducts();
    },
    fetchProducts() {
      this.productId = null;
      if (this.isLandlord() && this.selectedTenant !== null && this.user.data && typeof this.selectedTenant !== 'undefined') {
        this.$store.dispatch('landlord/products/fetchPermitProducts', { user: this.user, id: this.selectedTenant }).then(() => {
          this.hasProducts();
        });
      } else if (this.isTenant() && this.user.data) {
        this.$store.dispatch('landlord/products/fetchPermitProducts', { user: this.user, id: this.user.data.selectedRole.id }).then(() => {
          this.hasProducts();
        });
      } else {
        this.products.data = [];
      }
    },
    canAddPermitTo(product) {
      return product.permits_in_use < product.permits_count ||
        (this.permit.product && product.id === this.permit.product.id);
    },
    fetchUsers() {
      this.$store.dispatch('landlord/users/fetch', { user: this.user, limit: this.limit, orderBy: 'updated_at', keyword: this.usersKeywordFilter });
    },
    fetchAllData() {
      if (this.$route.name === 'add-user-with-permit') {
        this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
        this.fetchUsers();
        this.fetchProducts();
        this.selectedProduct = null;
        return;
      }
      this.fetchPermit();
      this.fetchUsers();
      this.fetchProducts();
    },
    closeConfirmationUncheckModal() {
      this.showConfirmationUncheckModal = false;
    },
    uncheckVehicleActive() {
      this.selectedVehicle = { id: null, vehicle_reg: null };
      this.closeConfirmationUncheckModal();
    },
    permitZoneAdd() {
      if (this.products.data) {
        this.permit.zone = '';
        this.products.data.forEach((product) => {
          const productAddress = product.address
            .replace(/\s+|\(.*?\)/g, '')
            .replace(/,/g, ', ');
          if (product.id === this.selectedProduct) {
            if (productAddress) {
              this.permit.zone = productAddress;
              this.showZone = true;
            } else {
              this.showZone = false;
            }
          }
        });
      }
    },
    fetchPermit() {
      if (this.$route.params.permitId && this.user.data) {
        const payload = {
          user: this.user,
          id: this.$route.params.permitId,
        };
        this.$store.dispatch('permits/permit/fetch', payload)
          .then(() => {
            if (!this.isUnderUser) {
              this.updateMap();
            }
            this.form = this.permit;
            this.setPermitRelatedVehicle();
            if (this.permit.product && this.permit.product.id && this.isUnderUser) {
              this.selectedProduct = this.permit.product.id;
              this.primordialSelectedProduct = this.selectedProduct;
            }
          });
      } else {
        this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
      }
    },
    setPermitRelatedVehicle() {
      if (this.permit.vehicle) {
        this.selectedVehicle = this.permit.vehicle;
        this.primordialSelectedVehicle = this.selectedVehicle;
      } else {
        this.selectedVehicle = { id: null, vehicle_reg: null };
      }
    },
    updateMap() {
      if (this.coordinates) {
        const bounds = L.geoJSON(this.coordinates).getBounds();
        if (bounds.isValid() && this.$refs.map) {
          this.$refs.map.fitBounds(bounds);
        }
      }
    },
    parsePhoneNumber(phone, code) {
      return phone
        ? format({ country: code, phone: phone.toString() }, 'International_plaintext')
        : '';
    },
    onSubmit() {
      this.$validator.validateAll()
        .then((success) => {
          if (success) {
            this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, null);

            if (this.selectedVehicle.new) {
              this.form.vehicle = { vehicleReg: this.selectedVehicle.vehicle_reg, comment: this.selectedVehicle.comment };
            } else if (this.selectedVehicle.id !== null) {
              this.form.vehicle = this.selectedVehicle;
            } else {
              delete this.form.vehicle;
            }

            if (!this.form.parkingCardNr || !this.canUseCards()) {
              this.form.parkingCardNr = null;
            }
            if (this.isUnderUser && this.selectedProduct) {
              this.form.productId = this.selectedProduct;
            }
            if (this.isUnderUser && this.selectedTenant) {
              this.form.tenantId = this.selectedTenant;
            }
            if (this.form.validFromDate && this.form.validFromDate !== '') {
              this.form.validFromDate = moment(this.form.validFromDate, 'DD.MM.YYYY').toDate();
            } else {
              this.form.validFromDate = moment().toDate();
            }
            if (this.form.expirationDate && this.form.expirationDate !== '') {
              this.form.expirationDate = moment(this.form.expirationDate, 'DD.MM.YYYY').toDate();
            } else {
              delete this.form.expirationDate;
            }
            this.form.userId = this.selectedUser;
            this.form.id = this.permit.id;

            if (this.form.vehicle && !this.form.vehicle.id) {
                this.$store.dispatch('permits/permit/saveNewVehicle', {
                  vehicle_reg: this.form.vehicle.vehicleReg.toUpperCase(),
                  comment: this.form.vehicle.comment,
                  userId: this.selectedUser,
                }).then((response) => {
                  this.form.vehicleId = response.body.data.id;
                  this.saveVehiclePermit();
                }).catch((error) => {
                  const { message } = error.body;
                  this.showWarningModal = true;
                  this.userWarningHeader = this.$t('common.warning');
                  this.userWarningBody = this.$t(`errors.${message}`);
                });
            } else {
                if(this.form.vehicle) {
                    this.form.vehicleId = this.form.vehicle.id;
                }
                this.saveVehiclePermit();
            }
          }
        });
    },
    saveVehiclePermit() {
      const clonedForm = JSON.parse(JSON.stringify(this.form));
      clonedForm.validFromDate = moment(this.form.validFromDate).format();
      if (clonedForm.expirationDate) {
        clonedForm.expirationDate = moment(this.form.expirationDate).format();
      }

      this.$store.dispatch('permits/permit/savePermit', { user: this.user, permit: clonedForm })
        .then(() => {
          if (!this.permitError && !this.permit.vehicleRegError) {
            this.$router.push({
              name: 'user',
              params: { id: this.$route.params.id },
            });
          } else {
            this.showWarningModal = true;
            this.userWarningHeader = this.$t('common.warning');
            if (this.permit.vehicleRegError) {
              this.userWarningHeader = this.permit.vehicleRegError;
            }
            if (this.permitError.body.parkingCardNr) {
              this.userWarningBody = this.$t('errors.valid_card_field');
            } else if (!this.tenantHasProducts && this.tenantPermitCount > 0) {
              this.userWarningBody = this.$t('errors.all_permits_issued', { permitCount: this.tenantPermitCount });
            } else if (this.permitError.body.productId) {
              this.userWarningBody = this.$t('errors.product_field_required');
            } else if (this.permitError.body.errors.productId) {
              this.userWarningBody = this.$t('errors.product_field_required');
            }
          }
        });
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    resetValidators() {
      this.$validator.remove('validParkingCardNr');
      this.$validator.clean();
    },
    createValidators() {
      this.createParkingCardValidator();
    },
    createParkingCardValidator() {
      this.$validator.extend('validParkingCardNr', {
        getMessage: field => `The ${field} value is not valid parking card number.`,
        validate: (value) => {
          if (value === '') {
            return true;
          }
          return /^[0-9A-F]{9}$/i.test(value);
        },
      });
    },
    showDeletingConfirmation() {
      this.showConfirmationModal = true;
    },
    cancelDelete() {
      this.showConfirmationModal = false;
    },
    deletePermit() {
      this.showConfirmationModal = false;
      this.$store.dispatch('permits/permit/delete', { id: this.permit.id, user: this.user })
        .then(() => {
          if (this.userError) {
            this.userWarningHeader = this.$t('common.deleting');
            this.userWarningBody = this.$t('users.delete_error');
            this.showWarningModal = true;
          } else {
            this.$store.commit(`landlord/permits/${mTypes.REMOVE_DATA}`, this.permit.id);
            this.$router.push({ name: 'user', params: { id: this.$route.params.id } });
            this.fetchProducts();
          }
        })
        .catch(() => {
          this.$store.commit(`landlord/permits/${mTypes.DATA_ERROR}`, true);
        });
    },
    canUseCards() {
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return this.products.data[i].can_use_cards;
          }
        }
      }
      return false;
    },
    canUseDateExpiration() {
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return !this.products.data[i].date_expiration;
          }
        }
      }
      return false;
    },
    canSetPermitStartDate() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return this.products.data[i].tenant_manage_permit_start;
          }
        }
      }
      return false;
    },
    canShowPermitSpot() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            if (!this.products.data[i].parking_spot_number &&
              !this.products.data[i].tenant_manage_permit_spot) {
              return false;
            }
          }
        }
      }
      return true;
    },
    canSetPermitSpot() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            if (this.products.data[i].parking_spot_number) {
              return false;
            }
            return this.products.data[i].tenant_manage_permit_spot;
          }
        }
      }
      return false;
    },
    canSetPermitExpirationDate() {
      if (this.isLandlord()) {
        return true;
      }
      if (this.selectedProduct != null && this.products.data) {
        for (let i = 0, l = this.products.data.length; i < l; i += 1) {
          if (this.products.data[i].id === this.selectedProduct) {
            return this.products.data[i].tenant_manage_permit_expiration;
          }
        }
      }
      return false;
    },
    getFormattedDateExpiration(date) {
      return moment(date).format('DD.MM.YYYY');
    },
    permitValidityPeriod(permit) {
      if (permit.validFromDate && permit.expirationDate) {
        return this.$t('end_user.permit_validity', { from: permit.validFromDate, to: permit.expirationDate });
      }
      return '';
    },
    isPermitExpired(permit) {
      if (!permit.expirationDate) {
        return false;
      }

      return moment().isAfter(moment(permit.expirationDate, 'DD.MM.YYYY'), 'day');
    },
    showListModalVehicle() {
      this.listModalType = 'vehicle';
    },
    showListModalProduct() {
      this.listModalType = 'product';
    },
    closeListModal() {
      if (this.listModalType === 'vehicle') {
        this.primordialSelectedVehicle = this.selectedVehicle;
      }
      if (this.listModalType === 'product') {
        if (this.primordialSelectedProduct) {
          this.selectedProduct = this.primordialSelectedProduct;
        } else {
          this.selectedProduct = null;
        }
      }
      this.listModalType = null;
    },
    confirmedListModal() {
      if (this.listModalType === 'product') {
        this.primordialSelectedProduct = this.selectedProduct;
      }
      this.listModalType = null;
    },
    confirmedVehicleSelection(vehicle) {
        this.selectedVehicle = vehicle;
        this.primordialSelectedVehicle = vehicle;
        this.listModalType = null;
    },
    setSelectedProductName(productId) {
      if (this.products.data) {
        this.products.data.forEach((item) => {
          if (item.id === productId) {
            this.selectedProductName = item.name;
            this.permitZoneAdd();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/app.scss";
@import "~styles/blocks/permit-edit.scss";
@import "~node_modules/leaflet/dist/leaflet.css";

.user-details-container {
  background-color: var(--white);
  display: inline-block;
  margin: 0 0 0 20px;
  padding: 10px 35px;
  flex: 1;

  @media screen and (max-width: 1074px) {
    padding: 10px;
  }

  @media screen and (max-width: 760px) {
    padding: 0;
    margin: 0;
  }

  >.content {
    @media screen and (max-width: 1074px) {
      flex-direction: column;
    }
  }

  .product-map {
    @media screen and (max-width: 1074px) {
      flex-direction: column;
      margin-left: 0;
    }
  }

  .details-table__row:nth-child(2) {
    .details-table__col {
      padding-top: 7px;
      vertical-align: top;
    }
  }

  .details-table__row {
    .details-table__col {
      position: relative;
      padding-top: 7px;

      .el-input {
        border-spacing: 0;
        max-width: 285px;
        position: relative;
      }

      .el-button:not(.vehicle-input) {
        width: 54px;
      }

      .el-button {
        span {
          white-space: normal;
        }
      }
    }
  }
}

.details-table {
  &.fixed-table {
    .details-table {
      &__row {
        display: flex;
        align-items: flex-end;
        flex-wrap: wrap;

        @media screen and (min-width: 1200px) {
          flex-wrap: nowrap;
        }
      }
    }
  }
}

.details-component {
  &__content-container {
    display: flex;

    @media screen and (max-width: 1600px) {
      flex-direction: column-reverse;
      align-items: flex-end;
    }
  }

  &__content {
    margin-right: 10px;

    @media screen and (max-width: 1600px) {
      margin: 0;
    }
  }
}

.permit-block {
  height: fit-content;
  min-width: 250px;
  box-shadow: none;
  margin: 0;

  @media screen and (max-width: 1600px) {
    width: 100%;
    margin-bottom: 9px;
  }

  &__zone {
    div {
      display: flex;
      align-content: center;

      &:not(:last-child) {
        margin-bottom: 5px;
      }
    }

    .permit-icon {
      display: inline-block;
      width: 16px;
      height: 16px;
      background-size: cover;
      margin-right: 10px;
    }
  }
}

.radiobuttons {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & label {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    & .radiobuttons__control {
      height: 31px;
    }

    & .radiobuttons__description {
      text-align: start;
    }
  }
}

.btn {
  &--change {
    display: inline-flex;
    text-transform: uppercase;
    background-color: var(--brand-green);
    color: var(--white);
  }
}
</style>

<style lang="scss">
@import "~styles/app.scss";
@import '~styles/blocks/permit.scss';

.el-button {
  vertical-align: middle;
}

.details-table {
  &__row {
    & .details-table__col {
      &--with-a-choise {
        display: flex;
        flex-wrap: wrap;
        height: auto !important;
        width: 100%;
      }

      .tooltip{
        left: 150px !important;
      }
    }

    .el-button {
      height: 40px;
      vertical-align: middle;

      span {
        margin: 0;
        white-space: normal;
      }
    }

    .el-input {
      &__suffix {
        z-index: 2;
        right: 0;
      }
    }

    .tooltip {
      right: auto;
      left: 50px;
      z-index: 2000;
      transform: translateY(-50%);
      max-height: 75px;

      &__content {
        @media screen and (max-width: 1000px) {
          max-height: none;
          height: auto;
          white-space: pre-wrap;
          max-width: 150px;
        }
      }
    }
  }
}

.form {
  .el-input {
    &.parkingCardNrInput-toggle {
      padding: 0;
    }

    &__inner {
      border: 0;
      padding: 25px 0 0;
      background-color: var(--grey-ten) !important;
      line-height: normal;

      &--vehicle-reg {
        max-width: fit-content;
        padding: 12px 40px 10px;
        margin-right: 10px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/users/AddEditPermitView.vue